import { useReducer, useCallback } from 'react';
import type { IClaimAiAnalysis, IClaimFormState, IClaimLineItemDraft } from '@/@types/claims';

export type { IClaimFormState } from '@/@types/claims';

type FormAction =
  | { type: 'SET_STEP'; step: number }
  | { type: 'SET_POLICY'; policyId: string }
  | { type: 'SET_FIELD'; field: keyof IClaimFormState; value: string }
  | { type: 'SET_CUSTOM_FIELD'; fieldKey: string; value: string }
  | { type: 'ADD_LINE_ITEM' }
  | { type: 'UPDATE_LINE_ITEM'; index: number; field: keyof IClaimLineItemDraft; value: string }
  | { type: 'REMOVE_LINE_ITEM'; index: number }
  | {
      type: 'ADD_DOC';
      doc: {
        storageId: string;
        fileName: string;
        fileSize: number;
        mimeType: string;
        documentType: string;
      };
    }
  | { type: 'PREFILL_FROM_AI'; data: IClaimAiAnalysis }
  | {
      type: 'LOAD_DRAFT';
      draft: Partial<IClaimFormState> & { policyId: string; currentStep: number };
    }
  | { type: 'RESET' };

const initialState: IClaimFormState = {
  currentStep: 0,
  policyId: '',
  treatmentType: '',
  dateOfService: '',
  dateOfInjuryOrIllness: '',
  diagnosis: '',
  symptoms: '',
  providerName: '',
  providerAddress: '',
  providerPhone: '',
  providerEmail: '',
  lineItems: [],
  bankName: '',
  bankAccountHolder: '',
  bankIban: '',
  bankSwift: '',
  reimburseTarget: 'insured',
  customFieldValues: {},
  uploadedDocs: [],
};

function formReducer(state: IClaimFormState, action: FormAction): IClaimFormState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.step };
    case 'SET_POLICY':
      return { ...state, policyId: action.policyId };
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_CUSTOM_FIELD':
      return {
        ...state,
        customFieldValues: { ...state.customFieldValues, [action.fieldKey]: action.value },
      };
    case 'ADD_LINE_ITEM':
      return {
        ...state,
        lineItems: [
          ...state.lineItems,
          {
            tempId: crypto.randomUUID(),
            description: '',
            dateOfService: '',
            amount: '',
            currency: 'EUR',
          },
        ],
      };
    case 'UPDATE_LINE_ITEM': {
      const updated = state.lineItems.map((item, i) =>
        i === action.index ? { ...item, [action.field]: action.value } : item
      );
      return { ...state, lineItems: updated };
    }
    case 'REMOVE_LINE_ITEM':
      return { ...state, lineItems: state.lineItems.filter((_, i) => i !== action.index) };
    case 'ADD_DOC':
      return { ...state, uploadedDocs: [...state.uploadedDocs, action.doc] };
    case 'PREFILL_FROM_AI': {
      const d = action.data;
      const aiLineItems: IClaimLineItemDraft[] = (d.lineItems ?? []).map(li => ({
        tempId: crypto.randomUUID(),
        description: li.description,
        dateOfService: li.dateOfService ?? '',
        amount: String(li.amount),
        currency: li.currency,
      }));
      return {
        ...state,
        treatmentType: d.treatmentType ?? state.treatmentType,
        dateOfService: d.dateOfService ?? state.dateOfService,
        providerName: d.providerName ?? state.providerName,
        providerAddress: d.providerAddress ?? state.providerAddress,
        diagnosis: d.diagnosis ?? state.diagnosis,
        symptoms: d.symptoms ?? state.symptoms,
        lineItems: aiLineItems.length > 0 ? aiLineItems : state.lineItems,
      };
    }
    case 'LOAD_DRAFT':
      return { ...initialState, ...action.draft };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export function useClaimForm() {
  const [state, dispatch] = useReducer(formReducer, initialState);

  const nextStep = useCallback(() => {
    dispatch({ type: 'SET_STEP', step: state.currentStep + 1 });
  }, [state.currentStep]);

  const prevStep = useCallback(() => {
    dispatch({ type: 'SET_STEP', step: Math.max(0, state.currentStep - 1) });
  }, [state.currentStep]);

  const goToStep = useCallback((step: number) => {
    dispatch({ type: 'SET_STEP', step });
  }, []);

  return { state, dispatch, nextStep, prevStep, goToStep };
}
